import { Button, HStack, Link, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContributingEntityStatus,
  useListContributingEntitiesQuery,
  useRemoveContributingEntityMutation,
  useResendContributingEntityInvitationMutation,
  type ContributingEntityDto,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useReportQueryError } from "../../../../../common/errors/useReportQueryError";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { ContributingEntityStatusTag, hasSubmittedAnswer } from "./ContributingEntityStatusTag";
import { useContributionUrls } from "../../contributing/useContributionUrls";

interface Props {
  bidId: string;
  onJumpToSupplier?: () => void;
}

export const OutstandingContributingEntities = ({ bidId, onJumpToSupplier }: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const confirmation = useConfirmationModal();
  const contributionUrls = useContributionUrls();
  const [resending, setResending] = useState<string | undefined>();
  const { data, error } = useListContributingEntitiesQuery({ bidId });
  const [resendInvitation] = useResendContributingEntityInvitationMutation();
  const [removeContributingEntity] = useRemoveContributingEntityMutation();

  useReportQueryError(error);

  const items = data?.items ?? [];
  const withStatus = (...statuses: ContributingEntityStatus[]) =>
    items.filter((entity) => statuses.includes(entity.status));

  const awaiting = withStatus(
    ContributingEntityStatus.NotSent,
    ContributingEntityStatus.InvitationSent,
    ContributingEntityStatus.InvitationAccepted,
  );
  const declined = withStatus(ContributingEntityStatus.InvitationDeclined);
  const incomplete = withStatus(ContributingEntityStatus.ResponseSubmitted);
  const withFindings = withStatus(ContributingEntityStatus.ResponseNotApproved);

  const remove = (entity: ContributingEntityDto) =>
    confirmation.show({
      title: t("Remove company"),
      description: t("{{name}} is removed from the offer and its answers are no longer counted.", {
        name: entity.name,
      }),
      confirmButtonText: t("Remove") ?? undefined,
      confirmButtonColor: "red",
      size: "md",
      onConfirmed: async () => {
        try {
          await removeContributingEntity({ bidId, entityId: entity.id }).unwrap();
        } catch (e) {
          displayer.trigger(e);
        }
      },
    });

  const resend = async (entityId: string) => {
    setResending(entityId);
    try {
      await resendInvitation({ bidId, entityId }).unwrap();
    } catch (e) {
      displayer.trigger(e);
    } finally {
      setResending(undefined);
    }
  };

  const section = (title: string, entities: ContributingEntityDto[], resendLabel?: string) =>
    entities.length > 0 && (
      <Stack spacing="2">
        <Text fontSize="sm" fontWeight="medium">
          {title}
        </Text>
        {entities.map((entity) => (
          <HStack key={entity.id} spacing="2" flexWrap="wrap">
            <Text fontSize="sm" fontWeight="medium">
              {entity.name}
            </Text>
            <ContributingEntityStatusTag
              status={entity.status}
              to={
                hasSubmittedAnswer(entity.status)
                  ? contributionUrls.review(bidId, entity.id)
                  : undefined
              }
            />
            {resendLabel && (
              <Button
                size="xs"
                variant="ghost"
                isLoading={resending === entity.id}
                onClick={() => resend(entity.id)}
              >
                {resendLabel}
              </Button>
            )}
            {!hasSubmittedAnswer(entity.status) && (
              <Button size="xs" variant="ghost" colorScheme="red" onClick={() => remove(entity)}>
                {t("Remove")}
              </Button>
            )}
          </HStack>
        ))}
      </Stack>
    );

  return (
    <Stack spacing="3">
      <Text fontSize="sm">{t("You can still deliver.")}</Text>

      {section(t("Still waiting for an answer"), awaiting, t("Send reminder") ?? undefined)}
      {section(t("Declined the invitation"), declined, t("Invite again") ?? undefined)}
      {section(t("Answered, but not complete"), incomplete)}
      {section(t("Answered, with findings the buyer will weigh"), withFindings)}

      {onJumpToSupplier && (
        <Link color="smPrimary" fontSize="sm" onClick={onJumpToSupplier}>
          {t("Go to supplier information")}
        </Link>
      )}
      {confirmation.modal}
    </Stack>
  );
};
